/**
 * Datos del tenant para la barra superior.
 *
 * El token trae sólo el id del tenant (`tenantId`, un claim propio: el discovery de
 * Identity no anuncia ningún claim con el nombre). El nombre se le pide a Identity
 * por id, y como la respuesta no está documentada se leen las formas razonables en
 * lugar de apostar por una: el objeto solo, una lista —así responde la ruta por
 * paciente que usa el portal— o el sobre `{ data }` del resto de las APIs.
 */

export interface TenantInfo {
    tenantId: string;
    tenantName: string;
}

const asTenant = (value: any): TenantInfo | undefined => {
    if (!value || typeof value !== 'object') return undefined;

    const tenantId = value.tenantId ?? value.id;
    const tenantName = value.tenantName ?? value.name;
    if (typeof tenantName !== 'string' || !tenantName.trim()) return undefined;

    return { tenantId: tenantId ? String(tenantId) : '', tenantName: tenantName.trim() };
};

/**
 * Lee el tenant de la respuesta, sea cual sea su forma. Si viene una lista, se
 * prefiere el que coincide con el id pedido: devolver otro tenant sería mostrar el
 * nombre de un centro que no es el del usuario.
 */
export const readTenant = (payload: unknown, tenantId?: string): TenantInfo | undefined => {
    const body: any = payload && typeof payload === 'object' && 'data' in (payload as any)
        ? (payload as any).data
        : payload;

    if (Array.isArray(body)) {
        const tenants = body.map(asTenant).filter((item): item is TenantInfo => !!item);
        if (!tenantId) return tenants[0];
        return tenants.find((item) => item.tenantId.toLowerCase() === tenantId.toLowerCase());
    }

    const tenant = asTenant(body);
    if (!tenant) return undefined;
    // El objeto solo no trae id: se asume que es el pedido, que es lo que respondió.
    return tenant.tenantId ? tenant : { ...tenant, tenantId: tenantId ?? '' };
};

/** Clave del cache por sesión: por tenant, así un cambio de cuenta no muestra el nombre anterior. */
export const tenantCacheKey = (tenantId: string): string => `prebiller.tenant.${tenantId.toLowerCase()}`;
